import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { EpiPictogram, RiskPictogram, VehiclePictogram, RulePictogram } from "@/components/PictogramList";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FileSpreadsheet, FileDown, ExternalLink, AlertTriangle, HeartPulse } from "lucide-react";
import { toast } from "sonner";

export default function Referentiel() {
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const [regles, setRegles] = useState([]);
  const [risques, setRisques] = useState([]);
  const [epi, setEpi] = useState([]);
  const [vehicules, setVehicules] = useState([]);
  const [produits, setProduits] = useState([]);
  const [fdsFhd, setFdsFhd] = useState([]);
  const [dangers, setDangers] = useState([]);
  const [produitSearch, setProduitSearch] = useState("");
  const [selectedProduit, setSelectedProduit] = useState(null);

  useEffect(() => {
    api.get("/referentiel/regles").then((res) => setRegles(res.data));
    api.get("/referentiel/risques").then((res) => setRisques(res.data));
    api.get("/referentiel/epi").then((res) => setEpi(res.data));
    api.get("/vehicules").then((res) => setVehicules(res.data));
    api.get("/produits").then((res) => setProduits(res.data));
    api.get("/fds-fhd").then((res) => setFdsFhd(res.data));
    api.get("/dangers-matieres").then((res) => setDangers(res.data));
  }, []);

  const produitsFiltres = produits.filter((p) => {
    const needle = produitSearch.toLowerCase();
    return !needle || p.Designation?.toLowerCase().includes(needle) || p.Famille?.toLowerCase().includes(needle);
  });
  const dangersDuProduit = selectedProduit ? dangers.filter((d) => d.Produit_ID === selectedProduit.Produit_ID) : [];
  const docsDuProduit = selectedProduit ? fdsFhd.filter((f) => f.Produit_ID === selectedProduit.Produit_ID) : [];

  const exportExcel = async () => {
    try {
      const res = await api.get("/referentiel/export-excel", { responseType: "blob" });
      const disposition = res.headers["content-disposition"] || "";
      const match = disposition.match(/filename="?([^"]+)"?/);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = match ? match[1] : "Referentiel_HSE.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Erreur export Excel");
    }
  };

  const exportPdf = async () => {
    try {
      const res = await api.get("/referentiel/export-pdf", { responseType: "blob" });
      const disposition = res.headers["content-disposition"] || "";
      const match = disposition.match(/filename="?([^"]+)"?/);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = match ? match[1] : "Referentiel_HSE.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Erreur export PDF");
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="referentiel-view">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">{t("nav.referential")}</h1>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" data-testid="btn-export-pdf" onClick={exportPdf}>
            <FileDown className="w-4 h-4" />
            {t("common.export_pdf")}
          </Button>
          <Button variant="outline" size="sm" data-testid="btn-export-excel" onClick={exportExcel}>
            <FileSpreadsheet className="w-4 h-4" />
            {t("common.export_excel")}
          </Button>
        </div>
      </div>
      <Tabs defaultValue={searchParams.get("tab") || "regles"}>
        <TabsList>
          <TabsTrigger value="regles" data-testid="ref-tab-regles">
            {t("common.tab_rules")} ({regles.length})
          </TabsTrigger>
          <TabsTrigger value="risques" data-testid="ref-tab-risques">
            {t("common.tab_risks")} ({risques.length})
          </TabsTrigger>
          <TabsTrigger value="epi" data-testid="ref-tab-epi">
            {t("common.tab_epi")} ({epi.length})
          </TabsTrigger>
          <TabsTrigger value="vehicules" data-testid="ref-tab-vehicules">
            {t("common.tab_vehicles")} ({vehicules.length})
          </TabsTrigger>
          <TabsTrigger value="produits" data-testid="ref-tab-produits">
            {t("common.tab_products")} ({produits.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="regles" className="space-y-2 pt-4">
          {regles
            .sort((a, b) => (a.Niveau_Referentiel || 99) - (b.Niveau_Referentiel || 99))
            .map((r) => (
              <div key={r.Regle_ID} className="flex items-start gap-3 border border-slate-200 dark:border-slate-800 rounded-lg p-3 text-sm" data-testid={`regle-item-${r.Regle_ID}`}>
                <RulePictogram regle={r} size={88} className="mt-0.5" />
                <div>
                  <p className="font-semibold text-slate-800 dark:text-slate-200">
                    {t("common.level")} {r.Niveau_Referentiel} · {r.Regle_ID} · {r.Libelle}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">{r.Source_Reglementaire}</p>
                  <p className="text-slate-600 dark:text-slate-400 mt-1">{r.Texte_Maitre_FR}</p>
                  <p className="text-xs text-slate-400 mt-1">
                    {r.Type_Vehicule} · {r.Type_Operation} · {r.Niveau_Obligation}
                  </p>
                </div>
              </div>
            ))}
        </TabsContent>

        <TabsContent value="risques" className="space-y-2 pt-4">
          {risques.map((r) => (
            <div key={r.Risque_ID} className="flex items-start gap-3 border border-slate-200 dark:border-slate-800 rounded-lg p-3 text-sm" data-testid={`risque-item-${r.Risque_ID}`}>
              <RiskPictogram risque={r} size={88} className="mt-0.5" />
              <div>
                <p className="font-semibold text-slate-800 dark:text-slate-200">
                  {r.Risque_ID} · {r.Libelle} <span className="text-xs font-normal text-amber-600 dark:text-amber-400">[{r.Criticite_Par_Defaut}]</span>
                </p>
                <p className="text-slate-600 dark:text-slate-400 mt-1">{r.Description}</p>
                <p className="text-xs text-slate-400 mt-1">{r.Mesure_Standard}</p>
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="epi" className="pt-4">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {epi.map((e) => (
              <div key={e.EPI_ID} className="flex items-start gap-3 border border-slate-200 dark:border-slate-800 rounded-lg p-3 text-sm" data-testid={`epi-item-${e.EPI_ID}`}>
                <EpiPictogram epi={e} size={96} />
                <div>
                  <p className="font-semibold text-slate-800 dark:text-slate-200">{e.Libelle}</p>
                  <p className="text-xs text-slate-500 mt-1">{e.Conditions_Application}</p>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="vehicules" className="pt-4">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {vehicules.map((v) => (
              <div key={v.Type_ID} className="flex items-start gap-3 border border-slate-200 dark:border-slate-800 rounded-lg p-3 text-sm" data-testid={`vehicule-item-${v.Type_ID}`}>
                <VehiclePictogram vehicule={v} size={104} />
                <div>
                  <p className="font-semibold text-slate-800 dark:text-slate-200">{v.Nom}</p>
                  <p className="text-slate-600 dark:text-slate-400 mt-1">{v.Description}</p>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="produits" className="space-y-3 pt-4">
          <Input
            data-testid="produit-search-input"
            placeholder={t("common.search")}
            value={produitSearch}
            onChange={(e) => setProduitSearch(e.target.value)}
            className="max-w-xs"
          />
          <div className="space-y-2">
            {produitsFiltres.map((p) => (
              <button
                key={p.Produit_ID}
                onClick={() => setSelectedProduit(p)}
                className="w-full text-left border border-slate-200 dark:border-slate-800 rounded-lg p-3 text-sm hover:border-emerald-400 hover:bg-emerald-50/40 dark:hover:bg-emerald-950/20 transition-colors"
                data-testid={`produit-item-${p.Produit_ID}`}
              >
                <p className="font-semibold text-slate-800 dark:text-slate-200">{p.Designation}</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  {p.Famille} · {p.Type_Produit_Dechet}
                </p>
              </button>
            ))}
            {produitsFiltres.length === 0 && (
              <p className="text-sm text-slate-500 p-4 text-center" data-testid="produits-empty-state">
                {t("common.no_results")}
              </p>
            )}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!selectedProduit} onOpenChange={(open) => !open && setSelectedProduit(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="dialog-produit-detail">
          <DialogHeader>
            <DialogTitle>{selectedProduit?.Designation}</DialogTitle>
          </DialogHeader>
          {selectedProduit && (
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                  {t("common.tab_risks")} & {t("common.tab_epi")}
                </h3>
                {dangersDuProduit.length === 0 ? (
                  <p className="text-sm text-slate-500" data-testid="dangers-empty-state">
                    {t("common.no_results")}
                  </p>
                ) : (
                  <div className="space-y-2">
                    {dangersDuProduit.map((d) => (
                      <div key={d.Danger_ID} className="border border-slate-200 dark:border-slate-800 rounded-lg p-3 text-sm" data-testid={`danger-item-${d.Danger_ID}`}>
                        <p className="font-medium text-slate-800 dark:text-slate-200">{d.Libelle_Danger}</p>
                        {d.Classification_CLP && <p className="text-xs text-rose-600 dark:text-rose-400 mt-1">CLP : {d.Classification_CLP}</p>}
                        {d.EPI_Requis && (
                          <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                            <span className="font-semibold">EPI/prévention :</span> {d.EPI_Requis}
                          </p>
                        )}
                        {d.Premiers_Secours && (
                          <p className="text-xs text-slate-600 dark:text-slate-400 mt-1 flex items-start gap-1">
                            <HeartPulse className="w-3 h-3 mt-0.5 shrink-0 text-rose-500" />
                            {d.Premiers_Secours}
                          </p>
                        )}
                        {d.ADR_Concerne && (
                          <span className="inline-block mt-1 text-xs font-semibold text-orange-700 dark:text-orange-400 bg-orange-50 dark:bg-orange-950/30 border border-orange-300 dark:border-orange-800 rounded-full px-2 py-0.5">
                            ADR {d.Numero_ONU ? `· ONU ${d.Numero_ONU}` : ""}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">FDS / FHD</h3>
                {docsDuProduit.length === 0 ? (
                  <p className="text-sm text-slate-500" data-testid="fds-fhd-empty-state">
                    {t("common.no_results")}
                  </p>
                ) : (
                  <div className="space-y-1.5">
                    {docsDuProduit.map((f) => (
                      <a
                        key={f.Document_ID}
                        href={f.URL_Drive}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center justify-between border border-slate-200 dark:border-slate-800 rounded-lg p-2.5 text-sm hover:bg-slate-50 dark:hover:bg-slate-800/40"
                        data-testid={`fds-fhd-doc-${f.Document_ID}`}
                      >
                        <span className="text-slate-700 dark:text-slate-300">
                          {f.Type_Document} · v{f.Version} · {f.Langue}
                        </span>
                        <ExternalLink className="w-4 h-4 text-slate-400" />
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
